/**
 * Frontend structured logging utility
 * Provides consistent logging interface for tenant-ui
 */

export interface LogContext {
  [key: string]: unknown;
}

export enum LogLevel {
  DEBUG = 'debug',
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error',
}

class Logger {
  private serviceName: string;
  private isDevelopment: boolean;

  constructor(serviceName: string = 'tenant-ui') {
    this.serviceName = serviceName;
    this.isDevelopment = process.env.NODE_ENV === 'development';
  }

  private formatMessage(
    level: LogLevel,
    message: string,
    context?: LogContext
  ) {
    const timestamp = new Date().toISOString();
    const logEntry = {
      timestamp,
      level,
      service: this.serviceName,
      message,
      ...context,
    };

    return logEntry;
  }

  private log(level: LogLevel, message: string, context?: LogContext) {
    const formattedEntry = this.formatMessage(level, message, context);

    // In development, keep readable console output
    if (this.isDevelopment) {
      const contextStr = context ? ` | ${JSON.stringify(context)}` : '';
      switch (level) {
        case LogLevel.DEBUG:
          console.debug(`[${level.toUpperCase()}] ${message}${contextStr}`);
          break;
        case LogLevel.INFO:
          console.info(`[${level.toUpperCase()}] ${message}${contextStr}`);
          break;
        case LogLevel.WARN:
          console.warn(`[${level.toUpperCase()}] ${message}${contextStr}`);
          break;
        case LogLevel.ERROR:
          console.error(`[${level.toUpperCase()}] ${message}${contextStr}`);
          break;
      }
    } else {
      // In production, use structured JSON logging
      console.log(JSON.stringify(formattedEntry));
    }
  }

  debug(message: string, context?: LogContext) {
    this.log(LogLevel.DEBUG, message, context);
  }

  info(message: string, context?: LogContext) {
    this.log(LogLevel.INFO, message, context);
  }

  warn(message: string, context?: LogContext) {
    this.log(LogLevel.WARN, message, context);
  }

  error(message: string, context?: LogContext) {
    this.log(LogLevel.ERROR, message, context);
  }
}

// Create singleton instance
const logger = new Logger('tenant-ui');

export default logger;
